import React, { useCallback, useEffect } from 'react'
import { css } from '@emotion/core'
import { ContentCheck, MenuClose, PersonRemove, ArrowCaret } from '@fs/zion-icon'
import { IconButton, Paragraph, Button, Row, colors, H2, Separator, useOverlay, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useSlsDispatchContext, useSlsHighlightsContext } from '@fs/zion-sls'
import ConfirmNotAPersonOverlay from '../../../../components/sharedReviewComponents/confirmNotAPersonOverlay/ConfirmNotAPersonOverlay'
import { useFullNameDispatchContext, useFullNameStateContext } from '../../providers/FullNameContext'
import { toggleNotAPersonHelper, showHighlights } from './ReviewStep1.utils'

const navButtonCss = css`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 0.25rem;
`

const ReviewStep1 = ({
  useTasksMethods,
  stepper,
  setFullNamedHighlighted,
  triggerTaskRegeneration,
  setError,
  handleSkip,
  resetSkipCounter,
}) => {
  const [t] = useTranslation()
  const notAPersonOverlay = useOverlay()
  const { currentTask, goToNextTask, loading } = useTasksMethods
  const { notAPerson } = currentTask
  const state = useFullNameStateContext()
  const dispatch = useFullNameDispatchContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const h = useSlsHighlightsContext()
  const pageWidth = usePageWidth()
  const isSmallScreen = pageWidth({ default: true, md: false })

  useEffect(() => {
    showHighlights(currentTask, isSmallScreen, h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask?.id, isSmallScreen])

  const handleYesClick = useCallback(() => {
    setFullNamedHighlighted(true)
    resetSkipCounter()
    stepper.goToStep(3)
  }, [stepper, setFullNamedHighlighted, resetSkipCounter])

  const handleNoClick = useCallback(() => {
    setFullNamedHighlighted(false)
    resetSkipCounter()
    h.setEditMode(true)
    stepper.goToStep(2)
  }, [stepper, setFullNamedHighlighted, h, resetSkipCounter])

  const handleSkipTask = useCallback(() => {
    handleSkip()
  }, [handleSkip])

  const toggleNotAPerson = useCallback(
    (notAPersonFlag) => {
      toggleNotAPersonHelper({
        notAPersonFlag,
        currentTask,
        dispatch,
        zionSlsDispatch,
        saveChangesFlag,
        triggerTaskRegeneration,
        setError,
        resetSkipCounter,
        userId: state.userId,
      })
    },
    [
      currentTask,
      dispatch,
      state.userId,
      zionSlsDispatch,
      saveChangesFlag,
      triggerTaskRegeneration,
      setError,
      resetSkipCounter,
    ]
  )

  const handleNotAPerson = useCallback(() => {
    toggleNotAPerson(!currentTask.notAPerson)
    setFullNamedHighlighted(false)
    goToNextTask(true)
    notAPersonOverlay.close()
  }, [notAPersonOverlay, setFullNamedHighlighted, toggleNotAPerson, goToNextTask, currentTask.notAPerson])

  const handleRestorePerson = useCallback(() => {
    toggleNotAPerson(false)
    resetSkipCounter()
  }, [toggleNotAPerson, resetSkipCounter])

  return (
    <>
      <H2 size="H5">{t('full-name-correction.step1.heading', 'Is the full name highlighted?')}</H2>
      <Separator size="xs" />
      <Paragraph size="sm">
        {t(
          'full-name-correction.step1.subHeading',
          "Check for prefixes, suffixes, maiden names, and married names. You'll be able to add or remove highlights in the next step."
        )}
      </Paragraph>
      <Separator size="sm" />
      <Row>
        <div css={navButtonCss}>
          <IconButton
            title={t('full-name-correction.step-one-yes', 'Yes')}
            Icon={ContentCheck}
            onClick={handleYesClick}
            linkName="Full Name: Step One Yes"
            color="green"
            emphasis="lightHigh"
            size="lg"
            disabled={loading || notAPerson}
          />
          <Paragraph centered size="sm">
            {t('full-name-correction.step-one-yes', 'Yes')}
          </Paragraph>
        </div>
        <div css={navButtonCss}>
          <IconButton
            title={t('full-name-correction.step-one-no', 'No')}
            Icon={MenuClose}
            onClick={handleNoClick}
            linkName="Full Name: Step One No"
            color="red"
            emphasis="lightHigh"
            size="lg"
            disabled={loading || notAPerson}
          />
          <Paragraph centered size="sm">
            {t('full-name-correction.step-one-no', 'No')}
          </Paragraph>
        </div>
        <div css={navButtonCss}>
          <IconButton
            title={
              notAPerson
                ? t('full-name-correction.step-one-next', 'Next')
                : t('full-name-correction.step-one-unsure', 'Unsure')
            }
            Icon={ArrowCaret}
            onClick={handleSkipTask}
            linkName={notAPerson ? 'Full Name: Step One Next' : 'Full Name: Step One Unsure'}
            color="blue"
            emphasis="lightHigh"
            size="lg"
            disabled={loading}
          />
          <Paragraph centered size="sm">
            {notAPerson
              ? t('full-name-correction.step-one-next', 'Next')
              : t('full-name-correction.step-one-unsure', 'Unsure')}
          </Paragraph>
        </div>
      </Row>
      <Separator size="xs" />
      <Button
        Icon={PersonRemove}
        onClick={notAPerson ? handleRestorePerson : notAPersonOverlay.handleClick}
        linkName={notAPerson ? 'Full Name: Restore Person' : 'Full Name: Not A Person'}
        color="blue"
        emphasis="low"
        size="sm"
        disabled={loading}
      >
        {notAPerson
          ? t('navigation.restore-nan-button', 'Restore Name')
          : t('navigation.not-a-person-button', 'Not a Person')}
      </Button>
      {notAPerson && (
        <Paragraph size="sm">
          <span style={{ color: colors.danger50 }}>
            {t(
              'full-name-correction.step1.restore-person-text',
              `This name was marked "Not a Person". Restore using the button above to work on the full name.`
            )}
          </span>
        </Paragraph>
      )}

      <ConfirmNotAPersonOverlay overlay={notAPersonOverlay} handleClickConfirmNotAPerson={handleNotAPerson} />
    </>
  )
}

export default ReviewStep1
